import type { Ref } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import {
  OnboardingVideoPlayer,
  type OnboardingVideoPlayerCopy,
} from '@/components/onboarding/OnboardingVideoPlayer';

interface OnboardingCourseOfferProps {
  headingRef?: Ref<HTMLHeadingElement>;
  eyebrow: string;
  title: string;
  description: string;
  detail: string;
  visualLabel: string;
  playerCopy: OnboardingVideoPlayerCopy;
  primaryLabel: string;
  secondaryLabel: string;
  pendingLabel: string;
  pending: boolean;
  onPrimary: () => void;
  onSecondary: () => void;
}

export const OnboardingCourseOffer = ({
  headingRef,
  eyebrow,
  title,
  description,
  detail,
  visualLabel,
  playerCopy,
  primaryLabel,
  secondaryLabel,
  pendingLabel,
  pending,
  onPrimary,
  onSecondary,
}: OnboardingCourseOfferProps) => (
  <section className="mx-auto grid max-w-5xl items-start gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10">
    <OnboardingVideoPlayer
      src="/videos/onboarding-viralize-v1.mp4"
      label={visualLabel}
      {...playerCopy}
    />

    <div className="text-center lg:text-left">
      <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary">
        {eyebrow}
      </p>
      <DialogTitle
        ref={headingRef}
        tabIndex={-1}
        className="mt-3 text-3xl font-semibold leading-tight tracking-[-0.035em] text-foreground focus:outline-none sm:text-4xl"
      >
        {title}
      </DialogTitle>
      <DialogDescription className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
        {description}
      </DialogDescription>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{detail}</p>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
        <Button
          type="button"
          size="lg"
          disabled={pending}
          onClick={onPrimary}
          className="min-w-52 bg-primary font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary"
        >
          {pending ? (
            <>
              <Loader2 className="animate-spin motion-reduce:animate-none" />
              {pendingLabel}
            </>
          ) : (
            <>
              {primaryLabel}
              <ArrowRight />
            </>
          )}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="ghost"
          disabled={pending}
          onClick={onSecondary}
          className="text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-primary"
        >
          {secondaryLabel}
        </Button>
      </div>
    </div>
  </section>
);
